import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Select, Tag } from "antd";
import { ArrowLeft } from "@phosphor-icons/react";
import Table from "../../../components/Table/Table";
import {
  ListViewWrapper,
  ListToolbar,
  ListTableWrapper,
  MutedText,
  MetaText,
  ResultName,
  UnsentPill,
  LinkBtn,
} from "../FeeNoticeQueueStyled";
import { formatDate } from "../utils";
import {
  fetchFeeNoticeTeamActivity,
  setFeeNoticeView,
  setTeamActivityDays,
} from "../../../slice/FeeNoticeSlice";
import { fetchFeeNoticeTeamActivityDetailAPI } from "../../../service/feeNotices";

const WINDOW_OPTIONS = [
  { value: 1, label: "Today" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

const METRIC_LABELS = {
  confirmed: "Confirmed",
  dismissed: "Dismissed",
  invoices: "Invoices",
  invoices_unsent: "Invoices created but NEVER SENT",
  sf_writes: "Salesforce writes",
};

const Zero = () => <MutedText>—</MutedText>;
const count = (n) =>
  n ? <ResultName style={{ fontSize: 13 }}>{n}</ResultName> : <Zero />;

// A metric cell that drills down when it has anything to show
// (Matthew, 2026-08-20: "if I click the 'Confirmed' block in Jorge's
// row, I want to see the documents he confirmed"). Zero-count cells
// stay inert — there is nothing behind them.
const DrillCell = ({ children, onClick, label }) =>
  onClick ? (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
        textDecoration: "underline dotted",
        textUnderlineOffset: 3,
      }}
    >
      {children}
    </button>
  ) : (
    children
  );

// `onMetricClick(agent, metric)` wires the four countable cells to the
// drill-down modal; passing null (or omitting) renders the same cells
// inert — that form is exported as `columns` for the row-render tests.
export const buildColumns = (onMetricClick) => {
  const drill = (agent, metric, hasWork) =>
    onMetricClick && hasWork ? () => onMetricClick(agent, metric) : null;

  return [
    {
      title: "Agent",
      key: "agent",
      width: "22%",
      render: (_, a) => (
        <div>
          <ResultName style={{ fontSize: 13 }}>{a.name}</ResultName>
          <MetaText style={{ display: "block" }}>{a.email}</MetaText>
        </div>
      ),
    },
    {
      title: "Confirmed",
      key: "confirmed",
      width: 130,
      render: (_, a) => (
        <DrillCell
          onClick={drill(a, "confirmed", a.confirmed > 0)}
          label={`Show letters ${a.name} confirmed`}
        >
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            {count(a.confirmed)}
            {a.confirmed_no_fee > 0 && (
              <MetaText>({a.confirmed_no_fee} no-fee)</MetaText>
            )}
          </div>
        </DrillCell>
      ),
    },
    {
      title: "Dismissed",
      key: "dismissed",
      width: 110,
      render: (_, a) => (
        <DrillCell
          onClick={drill(a, "dismissed", a.dismissed > 0)}
          label={`Show letters ${a.name} dismissed`}
        >
          {count(a.dismissed)}
        </DrillCell>
      ),
    },
    {
      title: "Invoices",
      key: "invoices",
      width: 190,
      render: (_, a) =>
        a.invoices_saved || a.invoices_sent ? (
          <div>
            <DrillCell
              onClick={drill(a, "invoices", true)}
              label={`Show invoices ${a.name} worked`}
            >
              <MetaText>
                {a.invoices_saved} created · {a.invoices_sent} sent
              </MetaText>
            </DrillCell>
            {/* Creating an invoice mints the client's live payment link,
                so one left unsent is a client billed in the ledger and
                never told. On 2026-08-28 three people created four
                between them and sent none, and no screen said so. */}
            {a.invoices_unsent > 0 && (
              <DrillCell
                onClick={drill(a, "invoices_unsent", true)}
                label={
                  `Show the ${a.invoices_unsent} invoice(s) `
                  + `${a.name} created but never sent`
                }
              >
                <UnsentPill>
                  {a.invoices_unsent} NOT SENT
                </UnsentPill>
              </DrillCell>
            )}
          </div>
        ) : (
          <Zero />
        ),
    },
    {
      title: "Salesforce writes",
      key: "sf",
      width: 140,
      render: (_, a) => (
        <DrillCell
          onClick={drill(a, "sf_writes", a.sf_writes_succeeded > 0)}
          label={`Show Salesforce writes by ${a.name}`}
        >
          {count(a.sf_writes_succeeded)}
        </DrillCell>
      ),
    },
    {
      title: "Working now",
      key: "working",
      width: 120,
      render: (_, a) =>
        a.working_now ? (
          <ResultName style={{ fontSize: 13 }}>
            {a.working_now} letter{a.working_now === 1 ? "" : "s"}
          </ResultName>
        ) : (
          <Zero />
        ),
    },
    {
      title: "Last activity",
      dataIndex: "last_action_at",
      key: "last",
      width: 170,
      render: (v) => (v ? formatDate(v) : <Zero />),
    },
  ];
};

export const columns = buildColumns(null);

const letterCol = {
  title: "Letter",
  key: "letter",
  render: (_, r) => (
    <ResultName style={{ fontSize: 13 }}>
      {r.letter || `Letter #${r.item_id}`}
    </ResultName>
  ),
};
const matterCol = {
  title: "Matter",
  key: "matter",
  render: (_, r) => (r.matter ? <MetaText>{r.matter}</MetaText> : <Zero />),
};
const whenCol = {
  title: "When",
  dataIndex: "when",
  key: "when",
  width: 170,
  render: (v) => (v ? formatDate(v) : <Zero />),
};

// What each metric's modal shows about a letter — the "some details"
// half of the request, chosen per metric rather than dumping the row.
export const DETAIL_COLUMNS = {
  confirmed: [
    letterCol,
    matterCol,
    {
      title: "Fee",
      key: "fee",
      width: 140,
      render: (_, r) =>
        r.no_fee ? (
          <Tag>no fee</Tag>
        ) : r.amount ? (
          <ResultName style={{ fontSize: 13 }}>${r.amount}</ResultName>
        ) : (
          <Zero />
        ),
    },
    {
      title: "Salesforce",
      key: "sf",
      width: 160,
      render: (_, r) =>
        r.no_fee ? (
          <MetaText>skipped (no fee)</MetaText>
        ) : r.sf_write_status === "succeeded" ? (
          <MetaText>{r.sf_object_id || "written"}</MetaText>
        ) : (
          <MetaText>{r.sf_write_status}</MetaText>
        ),
    },
    whenCol,
  ],
  dismissed: [
    letterCol,
    matterCol,
    {
      title: "Reason",
      key: "reason",
      render: (_, r) => (r.reason ? <MetaText>{r.reason}</MetaText> : <Zero />),
    },
    whenCol,
  ],
  invoices: [
    letterCol,
    matterCol,
    {
      title: "Invoice",
      key: "invoice",
      width: 140,
      render: (_, r) =>
        r.invoice_number ? <MetaText>{r.invoice_number}</MetaText> : <Zero />,
    },
    {
      title: "Action",
      key: "action",
      width: 200,
      render: (_, r) =>
        r.action === "sent" ? (
          <MetaText>sent{r.sent_to ? ` to ${r.sent_to}` : ""}</MetaText>
        ) : (
          <MetaText>created, not sent</MetaText>
        ),
    },
    {
      title: "Costs",
      key: "costs",
      width: 110,
      render: (_, r) =>
        r.total_costs ? (
          <ResultName style={{ fontSize: 13 }}>${r.total_costs}</ResultName>
        ) : (
          <Zero />
        ),
    },
    whenCol,
  ],
  invoices_unsent: [
    letterCol,
    matterCol,
    {
      title: "Invoice",
      key: "invoice",
      width: 140,
      render: (_, r) =>
        r.invoice_number ? <MetaText>{r.invoice_number}</MetaText> : <Zero />,
    },
    {
      title: "Client waiting",
      key: "client",
      width: 220,
      render: (_, r) => (
        <div>
          {r.client ? <ResultName>{r.client}</ResultName> : <Zero />}
          {r.client_email && <MetaText>{r.client_email}</MetaText>}
        </div>
      ),
    },
    {
      title: "Owed",
      key: "costs",
      width: 110,
      render: (_, r) =>
        r.total_costs ? (
          <ResultName style={{ fontSize: 13 }}>${r.total_costs}</ResultName>
        ) : (
          <Zero />
        ),
    },
    { ...whenCol, title: "Created" },
  ],
  sf_writes: [
    letterCol,
    matterCol,
    {
      title: "Salesforce record",
      key: "record",
      width: 180,
      render: (_, r) =>
        r.sf_object_id ? <MetaText>{r.sf_object_id}</MetaText> : <Zero />,
    },
    whenCol,
  ],
};

/**
 * Super-admin dashboard: who on the finance team is doing what —
 * confirms, dismissals, invoices saved/sent, Salesforce writes — over
 * a selectable window, plus the letters each agent holds right now.
 * Clicking any nonzero metric cell opens the drill-down modal listing
 * the letters behind that number, fetched for the same window.
 * The backend endpoints are role-gated (admin / super_admin); everyone
 * else never sees the entry points (ListView hides its button; the
 * Settings page is admin-only at the route level).
 *
 * Hosted in two places: inside the Fee Notices queue (with a back
 * link) and as the "Fee Notice Team Activity" Settings tab
 * (showBackLink={false} — there is no queue view to go back to).
 */
const TeamActivityView = ({ showBackLink = true }) => {
  const dispatch = useDispatch();
  const {
    teamActivity,
    teamActivityLoading,
    teamActivityError,
    teamActivityDays,
  } = useSelector((state) => state.feeNotices);

  // {agent, metric} while the drill-down modal is open. Detail rows
  // are modal-local state, not redux: they're ephemeral and scoped to
  // the click.
  const [drill, setDrill] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    dispatch(fetchFeeNoticeTeamActivity());
  }, [dispatch, teamActivityDays]);

  useEffect(() => {
    if (!drill) return undefined;
    let active = true;
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);
    fetchFeeNoticeTeamActivityDetailAPI({
      agentId: drill.agent.id,
      metric: drill.metric,
      days: teamActivityDays,
    })
      .then((response) => {
        if (active) setDetail(response?.data);
      })
      .catch((error) => {
        if (active) {
          setDetailError(error?.message || "Failed to load activity detail");
        }
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [drill, teamActivityDays]);

  const agents = teamActivity?.agents || [];
  const claims = teamActivity?.current_claims || [];
  const namesById = Object.fromEntries(agents.map((a) => [a.id, a.name]));
  const windowLabel = (
    WINDOW_OPTIONS.find((o) => o.value === teamActivityDays) || {}
  ).label;

  return (
    <ListViewWrapper>
      <ListToolbar>
        {showBackLink && (
          <LinkBtn onClick={() => dispatch(setFeeNoticeView("list"))}>
            <ArrowLeft size={14} weight="bold" /> Back to queue
          </LinkBtn>
        )}
        <Select
          value={teamActivityDays}
          onChange={(v) => dispatch(setTeamActivityDays(v))}
          options={WINDOW_OPTIONS}
          style={{ minWidth: 150, fontFamily: "Manrope" }}
          popupMatchSelectWidth={false}
        />
        {teamActivity && (
          <MetaText>
            {teamActivity.ready_unclaimed} letter
            {teamActivity.ready_unclaimed === 1 ? "" : "s"} ready and
            unclaimed
          </MetaText>
        )}
      </ListToolbar>

      {claims.length > 0 && (
        <MetaText style={{ display: "block", margin: "0 0 10px" }}>
          Right now:{" "}
          {claims
            .map(
              (c) =>
                `${namesById[c.agent_id] || `User #${c.agent_id}`} — ${
                  c.letter || `letter #${c.item_id}`
                }`
            )
            .join(" · ")}
        </MetaText>
      )}

      {teamActivityError ? (
        <MutedText>{teamActivityError}</MutedText>
      ) : (
        <ListTableWrapper>
          <Table
            columns={buildColumns((agent, metric) =>
              setDrill({ agent, metric })
            )}
            data={agents}
            loading={teamActivityLoading}
            emptyMessage="No fee notice activity in this window"
            // Tints the whole row when someone has invoices that never
            // went out, so it is spotted without reading a column.
            rowClassName={(a) =>
              a.invoices_unsent > 0 ? "row-invoices-unsent" : ""
            }
          />
        </ListTableWrapper>
      )}

      <Modal
        open={Boolean(drill)}
        onCancel={() => setDrill(null)}
        footer={null}
        width={860}
        title={
          drill
            ? `${drill.agent.name} — ${METRIC_LABELS[drill.metric]}${
                windowLabel ? ` (${windowLabel.toLowerCase()})` : ""
              }`
            : ""
        }
      >
        {detailError ? (
          <MutedText>{detailError}</MutedText>
        ) : (
          <>
            {detail && (
              <MetaText style={{ display: "block", margin: "0 0 10px" }}>
                {detail.count} item{detail.count === 1 ? "" : "s"}
              </MetaText>
            )}
            <Table
              columns={drill ? DETAIL_COLUMNS[drill.metric] : []}
              data={detail?.items || []}
              loading={detailLoading}
              emptyMessage="Nothing in this window"
            />
          </>
        )}
      </Modal>
    </ListViewWrapper>
  );
};

export default TeamActivityView;
